import { widgetStyles } from './styles.js';
import { ApiClient, type AssistantAction, type ChatResponse, type PublicConfig } from './api-client.js';
import { storage } from './storage.js';

export interface WidgetConfig {
  businessId: string;
  apiUrl: string;
  primaryColor?: string;
  position?: 'bottom-right' | 'bottom-left';
  initialMessage?: string;
  /** Texto del bocadillo CTA junto a la burbuja (llamada a la acción). */
  ctaText?: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'bot';
  text: string;
  citedSources?: ChatResponse['citedSources'];
}

const ICON_CHAT = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>`;
const ICON_CLOSE_LG =`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
const ICON_RESET = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>`;

export class AsesoriaWidget {
  private host: HTMLElement;
  private shadow: ShadowRoot;
  private root!: HTMLElement;
  private bubble!: HTMLButtonElement;
  private panel?: HTMLElement;
  private messagesEl?: HTMLElement;
  private composerInput?: HTMLTextAreaElement;
  private actionsEl?: HTMLElement;
  private sendBtn?: HTMLButtonElement;
  private footerMeta?: HTMLElement;

  private api: ApiClient;
  private cfg: WidgetConfig;
  private serverConfig?: PublicConfig;
  private messages: ChatMessage[] = [];
  private conversationId?: string;
  private visitorId?: string;
  private opened = false;
  private isLoading = false;

  constructor(config: WidgetConfig) {
    this.cfg = config;
    this.api = new ApiClient(config.apiUrl.replace(/\/$/, ''), config.businessId);
    this.host = document.createElement('div');
    this.host.id = 'asesoria-chatbot-host';
    this.shadow = this.host.attachShadow({ mode: 'open' });
    document.body.appendChild(this.host);
    this.render();
    this.loadConfig()
      .catch((e) => console.warn('[Asesoría chatbot] config no cargada:', e))
      .finally(() => this.maybeAutoOpen());
  }

  /** Auto-abre el chat si la URL lo pide (p.ej. QR de la puerta: icasesores.com/?chat=open o #chat). */
  private maybeAutoOpen() {
    try {
      const wantsOpen =
        new URLSearchParams(window.location.search).get('chat') === 'open' ||
        window.location.hash === '#chat';
      if (wantsOpen && !this.opened) this.open();
    } catch {
      /* sin window.location → no auto-abrir */
    }
  }

  private async loadConfig() {
    try {
      this.serverConfig = await this.api.getConfig();
      if (this.serverConfig.primaryColor) {
        this.root.style.setProperty('--asesoria-primary', this.serverConfig.primaryColor);
      }
      this.renderFooterMeta();
    } catch (e) {
      // Fallback graceful: usar config local
      this.serverConfig = {
        businessId: this.cfg.businessId,
        slug: this.cfg.businessId,
        name: 'Asesoría',
        primaryColor: this.cfg.primaryColor ?? '#0F766E',
        assistantName: 'Asistente',
        welcomeMessage: this.cfg.initialMessage ?? 'Hola, ¿en qué puedo ayudarte?',
        disclaimerText: 'Información general, no sustituye al asesoramiento profesional.',
        rgpdConsentText: '',
        appointmentEnabled: true,
        leadCaptureEnabled: true,
        quickReplies: [],
        emergencyMessage: '',
      };
    }
  }

  private renderFooterMeta() {
    const meta = this.footerMeta;
    if (!meta) return;
    const url = this.serverConfig?.privacyPolicyUrl;
    let safeUrl: string | undefined;
    if (url) {
      try {
        const u = new URL(url);
        if (u.protocol === 'http:' || u.protocol === 'https:') safeUrl = u.href;
      } catch {
        /* URL inválida → caemos a texto plano */
      }
    }
    if (safeUrl) {
      meta.textContent = 'Tus datos se tratan según la ';
      const a = document.createElement('a');
      a.href = safeUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = 'política de privacidad';
      meta.appendChild(a);
      meta.appendChild(document.createTextNode(' de la asesoría.'));
    } else {
      meta.textContent = 'Tus datos se tratan según la política de privacidad de la asesoría.';
    }
  }

  private render() {
    const style = document.createElement('style');
    style.textContent = widgetStyles;
    this.shadow.appendChild(style);

    this.root = document.createElement('div');
    this.root.className = `widget-root position-${this.cfg.position ?? 'bottom-right'}`;
    if (this.cfg.primaryColor) this.root.style.setProperty('--asesoria-primary', this.cfg.primaryColor);

    this.bubble = document.createElement('button');
    this.bubble.className = 'bubble';
    this.bubble.setAttribute('aria-label', 'Abrir chat de asesoría');
    this.bubble.innerHTML = ICON_CHAT;
    this.bubble.addEventListener('click', () => this.toggle());
    this.root.appendChild(this.bubble);

    this.maybeRenderCta();
    this.shadow.appendChild(this.root);
  }

  /** Bocadillo de texto junto a la burbuja para invitar a escribir. Se puede cerrar (se recuerda). */
  private ctaLabel?: HTMLElement;
  private maybeRenderCta() {
    let dismissed = false;
    try {
      dismissed = localStorage.getItem('asesoria-chatbot:cta-dismissed') === '1';
    } catch {
      /* sin localStorage → mostramos igual */
    }
    if (dismissed) return;
    const cta = document.createElement('div');
    cta.className = 'cta-label';
    const span = document.createElement('span');
    span.textContent = this.cfg.ctaText ?? '💬 ¿Tienes dudas? Pregúntame';
    const close = document.createElement('button');
    close.className = 'cta-close';
    close.setAttribute('aria-label', 'Cerrar aviso');
    close.innerHTML = '&times;';
    close.addEventListener('click', (e) => {
      e.stopPropagation();
      this.dismissCta();
    });
    cta.append(span, close);
    cta.addEventListener('click', () => this.open());
    this.ctaLabel = cta;
    this.root.appendChild(cta);
  }

  private dismissCta() {
    this.ctaLabel?.remove();
    this.ctaLabel = undefined;
    try {
      localStorage.setItem('asesoria-chatbot:cta-dismissed', '1');
    } catch {
      /* ignore */
    }
  }

  private toggle() {
    if (this.opened) this.close();
    else this.open();
  }

  private async open() {
    this.opened = true;
    this.ctaLabel?.classList.add('cta-hidden');
    this.bubble.classList.add('is-open');
    this.bubble.innerHTML = ICON_CLOSE_LG;
    this.bubble.setAttribute('aria-label', 'Cerrar chat');
    this.buildPanel();
    // Accesibilidad: cierre con Escape y foco al campo de escritura.
    this.panel?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Escape') this.close();
    });
    this.attachViewportHandler();
    (this.panel?.querySelector('textarea, input[type="text"]') as HTMLElement | null)?.focus();
    if (!this.conversationId) {
      await this.startConversation();
    }
  }

  private close() {
    this.opened = false;
    this.detachViewportHandler();
    if (this.panel) {
      this.panel.remove();
      this.panel = undefined;
    }
    this.bubble.classList.remove('is-open');
    this.bubble.innerHTML = ICON_CHAT;
    this.bubble.setAttribute('aria-label', 'Abrir chat de asesoría');
    this.bubble.focus();
  }

  // En móvil (panel a pantalla completa), iOS Safari encoge el visualViewport al
  // abrir el teclado pero NO reposiciona los elementos fixed → el composer quedaba
  // bajo el teclado. Ajustamos la altura del panel al área visible real.
  private vvHandler?: () => void;
  private attachViewportHandler() {
    const vv = window.visualViewport;
    if (!vv) return;
    this.vvHandler = () => {
      if (!this.panel) return;
      if (window.innerWidth > 480) {
        this.panel.style.height = '';
        this.panel.style.top = '';
        return;
      }
      // Anclar el panel EXACTAMENTE al área visible: alto + desplazamiento
      // (offsetTop) del teclado/scroll, para que cubra toda la pantalla sin
      // dejar ver la web detrás ni quedarse "a media altura".
      this.panel.style.height = `${Math.round(vv.height)}px`;
      this.panel.style.top = `${Math.round(vv.offsetTop)}px`;
    };
    vv.addEventListener('resize', this.vvHandler);
    vv.addEventListener('scroll', this.vvHandler);
    this.vvHandler();
  }
  private detachViewportHandler() {
    const vv = window.visualViewport;
    if (vv && this.vvHandler) {
      vv.removeEventListener('resize', this.vvHandler);
      vv.removeEventListener('scroll', this.vvHandler);
    }
    this.vvHandler = undefined;
  }

  private buildPanel() {
    const panel = document.createElement('div');
    panel.className = 'panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Chat asistente de asesoría');

    // Header
    const header = document.createElement('div');
    header.className = 'header';
    const apiBase = this.cfg.apiUrl.replace(/\/$/, '');
    header.innerHTML = `
      <div class="avatar" aria-hidden="true">
        <img src="${apiBase}/logo.png" alt="" onerror="this.style.display='none'" />
      </div>
      <div class="titles">
        <div class="name">${this.escape(this.serverConfig?.name ?? 'Asesoría')}</div>
        <div class="status">
          <span class="status-dot" aria-hidden="true"></span>
          <span class="status-text">Online 24/7 · Responde al instante</span>
        </div>
      </div>
      <button class="reset-btn" aria-label="Reiniciar conversación" title="Reiniciar">${ICON_RESET}</button>
    `;
    header.querySelector('.reset-btn')!.addEventListener('click', () => this.resetConversation());
    panel.appendChild(header);

    // Messages
    this.messagesEl = document.createElement('div');
    this.messagesEl.className = 'messages';
    panel.appendChild(this.messagesEl);

    // Quick reply / actions
    this.actionsEl = document.createElement('div');
    this.actionsEl.className = 'options';
    panel.appendChild(this.actionsEl);

    // Composer
    const composer = document.createElement('form');
    composer.className = 'composer';
    composer.innerHTML = `
      <textarea
        rows="1"
        maxlength="2000"
        placeholder="Escribe tu mensaje…"
        aria-label="Mensaje"
      ></textarea>
      <input class="honeypot" type="text" tabindex="-1" autocomplete="off" name="company" aria-hidden="true" />
      <button class="send-btn" type="submit" aria-label="Enviar">Enviar</button>
    `;
    this.composerInput = composer.querySelector('textarea') as HTMLTextAreaElement;
    this.sendBtn = composer.querySelector('.send-btn') as HTMLButtonElement;
    this.composerInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSubmit();
      }
    });
    this.composerInput.addEventListener('input', () => this.autoresize());
    composer.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });
    panel.appendChild(composer);

    const meta = document.createElement('div');
    meta.className = 'footer-meta';
    this.footerMeta = meta;
    this.renderFooterMeta();
    panel.appendChild(meta);

    this.panel = panel;
    this.root.appendChild(panel);

    // Pintar histórico si hay
    this.messages.forEach((m) => this.appendMessageDom(m));
    this.scrollDown();
  }

  private autoresize() {
    if (!this.composerInput) return;
    this.composerInput.style.height = 'auto';
    this.composerInput.style.height = Math.min(this.composerInput.scrollHeight, 120) + 'px';
  }

  private async startConversation() {
    try {
      const sourceUrl = window.location.href.slice(0, 500);
      const start = await this.api.startConversation(storage.visitorId, sourceUrl);
      this.conversationId = start.conversationId;
      this.visitorId = start.visitorId;
      storage.visitorId = this.visitorId;
      storage.conversationId = this.conversationId;

      const welcome = this.cfg.initialMessage ?? start.welcomeMessage ?? this.serverConfig?.welcomeMessage;
      if (welcome) {
        this.appendMessage({ id: this.uid(), role: 'bot', text: welcome });
      }
      const quick = start.quickReplies ?? this.serverConfig?.quickReplies ?? [];
      if (quick.length > 0) this.renderActions([{ type: 'quick_replies', items: quick }]);
    } catch (e) {
      this.showError('No se ha podido conectar con el servidor. Inténtalo de nuevo más tarde.');
    }
  }

  private async handleSubmit() {
    if (!this.composerInput || !this.conversationId || this.isLoading) return;
    const text = this.composerInput.value.trim();
    if (!text) return;
    this.composerInput.value = '';
    this.autoresize();
    // Limpiar quick replies / formularios visibles en cuanto el usuario decide escribir,
    // sin esperar a la respuesta del servidor.
    this.clearActions();
    this.appendMessage({ id: this.uid(), role: 'user', text });
    await this.sendToServer(text);
  }

  private async sendToServer(text: string) {
    if (!this.conversationId) return;
    this.isLoading = true;
    this.toggleLoading(true);
    try {
      const r = await this.api.sendMessage(this.conversationId, text);
      this.appendMessage({ id: this.uid(), role: 'bot', text: r.message, citedSources: r.citedSources });
      if (r.actions && r.actions.length > 0) this.renderActions(r.actions);
      else this.clearActions();
    } catch (e) {
      this.showError('Error al enviar el mensaje. ¿Quieres intentarlo de nuevo?');
    } finally {
      this.isLoading = false;
      this.toggleLoading(false);
    }
  }

  private renderActions(actions: AssistantAction[]) {
    if (!this.actionsEl) return;
    this.actionsEl.innerHTML = '';
    for (const a of actions) {
      switch (a.type) {
        case 'quick_replies': {
          const items: string[] = a.items ?? [];
          for (const item of items) {
            const chip = document.createElement('button');
            chip.className = 'chip';
            chip.type = 'button';
            chip.textContent = item;
            chip.addEventListener('click', () => {
              this.appendMessage({ id: this.uid(), role: 'user', text: item });
              this.clearActions();
              this.sendToServer(item);
            });
            this.actionsEl!.appendChild(chip);
          }
          break;
        }
        case 'lead_form': {
          this.renderLeadForm(a);
          break;
        }
        case 'appointment_types': {
          const types = (a as any).types as Array<{ slug: string; name: string; durationMinutes: number; description?: string }>;
          const wrap = document.createElement('div'); wrap.className = 'options';
          for (const t of types) {
            const c = document.createElement('button');
            c.className = 'chip';
            c.type = 'button';
            c.textContent = `${t.name} (${t.durationMinutes} min)`;
            c.title = t.description ?? '';
            c.addEventListener('click', () => {
              this.appendMessage({ id: this.uid(), role: 'user', text: t.name });
              this.clearActions();
              this.sendToServer(t.name);
            });
            wrap.appendChild(c);
          }
          this.actionsEl!.appendChild(wrap);
          break;
        }
        case 'appointment_slots': {
          const slots = (a as any).slots as Array<{ startsAt: string; label: string }>;
          const wrap = document.createElement('div'); wrap.className = 'slots';
          slots.forEach((s) => {
            const b = document.createElement('button');
            b.className = 'slot';
            b.type = 'button';
            b.textContent = s.label;
            b.addEventListener('click', () => {
              this.appendMessage({ id: this.uid(), role: 'user', text: s.label });
              this.clearActions();
              // Enviamos el ISO directamente al server para que asocie el slot al state
              this.sendToServer(s.startsAt);
            });
            wrap.appendChild(b);
          });
          this.actionsEl!.appendChild(wrap);
          break;
        }
        case 'appointment_confirmed': {
          const note = document.createElement('div');
          note.className = 'cited-sources';
          note.textContent = `Cita confirmada: ${(a as any).label ?? ''}`;
          this.actionsEl!.appendChild(note);
          break;
        }
        case 'lead_captured': {
          // info silenciosa
          break;
        }
        case 'conversation_complete': {
          this.renderConversationComplete((a as any).message ?? 'Hemos guardado su consulta. Un asesor le contactará en breve.');
          break;
        }
      }
    }
  }

  private renderConversationComplete(message: string) {
    if (!this.actionsEl) return;
    this.actionsEl.innerHTML = '';
    const box = document.createElement('div');
    box.className = 'conversation-end';
    const icon = document.createElement('div');
    icon.className = 'conversation-end-icon';
    icon.textContent = '✓';
    const text = document.createElement('div');
    text.className = 'conversation-end-text';
    text.textContent = message;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'send-btn restart-btn';
    btn.textContent = 'Iniciar nueva consulta';
    btn.addEventListener('click', () => this.resetConversation());
    box.appendChild(icon);
    box.appendChild(text);
    box.appendChild(btn);
    this.actionsEl.appendChild(box);
    this.setComposerDisabled(true);
    this.scrollDown();
  }

  private setComposerDisabled(disabled: boolean) {
    if (this.composerInput) {
      this.composerInput.disabled = disabled;
      this.composerInput.placeholder = disabled
        ? 'Conversación finalizada. Inicie una nueva para continuar.'
        : 'Escribe tu mensaje…';
    }
    if (this.sendBtn) this.sendBtn.disabled = disabled;
  }

  private clearActions() {
    if (this.actionsEl) this.actionsEl.innerHTML = '';
  }

  private renderLeadForm(a: any) {
    if (!this.actionsEl) return;
    const fields: Array<{ name: string; label: string; required: boolean; type?: string; placeholder?: string }> = a.fields ?? [];
    const consentText: string = a.consentText ?? 'Acepto el tratamiento de mis datos según la política de privacidad.';
    const privacyUrl: string | undefined = a.privacyPolicyUrl;
    const submitLabel: string = a.submitLabel ?? 'Enviar';
    const apptSlug: string | undefined = a.appointmentTypeSlug;
    const apptIso: string | undefined = a.appointmentStartsAt;

    const form = document.createElement('form');
    form.className = 'lead-form';
    form.setAttribute('aria-label', 'Formulario de contacto');

    for (const f of fields) {
      const row = document.createElement('div');
      row.className = 'lead-form-row';
      const label = document.createElement('label');
      label.textContent = f.label + (f.required ? ' *' : '');
      const input = document.createElement('input');
      input.name = f.name;
      input.type = f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : 'text';
      input.required = f.required;
      input.placeholder = f.placeholder ?? '';
      input.maxLength = f.name === 'message' ? 1000 : 200;
      input.autocomplete = f.name === 'email' ? 'email' : f.name === 'name' ? 'name' : f.name === 'phone' ? 'tel' : 'off';
      row.appendChild(label);
      row.appendChild(input);
      form.appendChild(row);
    }

    // Honeypot oculto
    const hp = document.createElement('input');
    hp.type = 'text'; hp.name = 'company'; hp.tabIndex = -1; hp.autocomplete = 'off';
    hp.className = 'honeypot';
    hp.setAttribute('aria-hidden', 'true');
    form.appendChild(hp);

    // Consent checkbox
    const consentRow = document.createElement('div');
    consentRow.className = 'lead-form-consent';
    const cbId = 'asesoria-consent-' + this.uid();
    const cb = document.createElement('input');
    cb.type = 'checkbox'; cb.id = cbId; cb.required = true;
    const cbLabel = document.createElement('label');
    cbLabel.htmlFor = cbId;
    cbLabel.textContent = consentText + ' ';
    if (privacyUrl) {
      const a2 = document.createElement('a');
      a2.href = privacyUrl; a2.target = '_blank'; a2.rel = 'noopener noreferrer';
      a2.textContent = 'Ver política';
      cbLabel.appendChild(a2);
    }
    consentRow.appendChild(cb);
    consentRow.appendChild(cbLabel);
    form.appendChild(consentRow);

    // Submit
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'send-btn lead-form-submit';
    submit.textContent = submitLabel;
    form.appendChild(submit);

    const errorEl = document.createElement('div');
    errorEl.className = 'lead-form-error';
    form.appendChild(errorEl);

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      errorEl.textContent = '';
      if (!cb.checked) {
        errorEl.textContent = 'Debes aceptar el tratamiento de datos para continuar.';
        return;
      }
      if (hp.value) return; // honeypot
      const data: Record<string, string | undefined> = {};
      for (const f of fields) {
        const el = form.querySelector(`input[name="${f.name}"]`) as HTMLInputElement | null;
        const v = el?.value.trim() ?? '';
        if (f.required && !v) { errorEl.textContent = `El campo "${f.label}" es obligatorio.`; return; }
        if (v) data[f.name] = v;
      }
      submit.disabled = true; submit.textContent = 'Enviando…';
      try {
        const resp = await this.api.submitChatForm({
          conversationId: this.conversationId!,
          name: data.name!,
          email: data.email!,
          phone: data.phone,
          topic: data.topic,
          consentAccepted: true,
          appointmentTypeSlug: apptSlug,
          appointmentStartsAt: apptIso,
        });
        // Mostrar respuesta del bot directamente
        this.clearActions();
        this.appendMessage({ id: this.uid(), role: 'user', text: '✓ Datos enviados' });
        this.appendMessage({ id: this.uid(), role: 'bot', text: resp.message });
      } catch (err) {
        errorEl.textContent = 'No se han podido enviar los datos. Intenta de nuevo en un momento.';
        submit.disabled = false; submit.textContent = submitLabel;
      }
    });

    this.actionsEl.appendChild(form);
    this.scrollDown();
  }

  private appendMessage(m: ChatMessage) {
    this.messages.push(m);
    this.appendMessageDom(m);
    this.scrollDown();
  }

  private appendMessageDom(m: ChatMessage) {
    if (!this.messagesEl) return;
    const wrap = document.createElement('div');
    wrap.className = `msg ${m.role === 'user' ? 'user' : 'bot'}`;
    const bubble = document.createElement('div');
    bubble.className = 'bubble-msg';
    bubble.textContent = m.text;
    wrap.appendChild(bubble);
    this.messagesEl.appendChild(wrap);
  }

  private toggleLoading(loading: boolean) {
    if (this.sendBtn) this.sendBtn.disabled = loading;
    if (!this.messagesEl) return;
    const existing = this.messagesEl.querySelector('.typing');
    if (loading && !existing) {
      const t = document.createElement('div');
      t.className = 'typing';
      t.innerHTML = '<span></span><span></span><span></span>';
      this.messagesEl.appendChild(t);
      this.scrollDown();
    } else if (!loading && existing) {
      existing.remove();
    }
  }

  private scrollDown() {
    if (!this.messagesEl) return;
    requestAnimationFrame(() => {
      this.messagesEl!.scrollTop = this.messagesEl!.scrollHeight;
    });
  }

  private showError(msg: string) {
    if (!this.panel) return;
    const banner = document.createElement('div');
    banner.className = 'error-banner';
    banner.textContent = msg;
    this.panel.insertBefore(banner, this.panel.firstChild?.nextSibling ?? null);
    setTimeout(() => banner.remove(), 5000);
  }

  private async resetConversation() {
    storage.reset();
    this.conversationId = undefined;
    this.messages = [];
    if (this.messagesEl) this.messagesEl.innerHTML = '';
    this.clearActions();
    this.setComposerDisabled(false);
    if (this.composerInput) this.composerInput.value = '';
    await this.startConversation();
  }

  private uid() {
    return Math.random().toString(36).slice(2, 10);
  }

  private escape(s: string) {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
